import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { RefreshIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { PreviousBookingsStepClientProfile$key } from "./__generated__/PreviousBookingsStepClientProfile.graphql";
import { PreviousBookingsStep_TimeSlot$key } from "./__generated__/PreviousBookingsStep_TimeSlot.graphql";
import { PreviousBookingsList } from "./components/PreviousBookingsList";

type PreviousBookingsStepProps = {
  previousBookingsRef: PreviousBookingsStepClientProfile$key | null;
  previousBookingsTimeSlotRef: PreviousBookingsStep_TimeSlot$key | null;
  onClose: () => void;
};

export const PreviousBookingsStep = ({
  previousBookingsRef,
  previousBookingsTimeSlotRef,
  onClose,
}: PreviousBookingsStepProps) => {
  const timeSlot = useFragment(
    PreviousBookingsStepTimeSlotFragmentString,
    previousBookingsTimeSlotRef
  );
  const data = useFragment(
    PreviousBookingsStepFragmentString,
    previousBookingsRef
  );
  const allowedNumberOfPlayers =
    (timeSlot?.capacity ?? 4) - (timeSlot?.usedCapacity ?? 0);

  return (
    <Box flex={1} bg="surface-secondary">
      <PreviousBookingsHeader />
      <PreviousBookingsList
        clientRef={data?.member ?? null}
        onClose={onClose}
        allowedNumberOfPlayers={allowedNumberOfPlayers}
      />
    </Box>
  );
};

const PreviousBookingsHeader = () => {
  return (
    <HStack
      py={3}
      px={5}
      borderBottomColor={"outline-default"}
      borderBottomWidth={1}
      width="100%"
      gap={4}
      alignItems="center"
      bg="surface-primary"
    >
      <VStack
        width={32}
        height={32}
        borderRadius="6"
        justifyContent="center"
        alignItems="center"
        borderWidth={1}
        borderColor="outline-default"
        shadowColor="black"
        shadowOffset={{ width: 0, height: 2 }}
        shadowOpacity={0.08}
        shadowRadius={2}
      >
        <RefreshIcon />
      </VStack>
      <VStack flexShrink={1}>
        <Text variant="mainBaseSemiBold">{t`Repeat Booking`}</Text>
        <Text
          variant="mainSmallMedium"
          color="text-secondary"
        >{t`Reuse preferences from your recent bookings to save time.`}</Text>
      </VStack>
    </HStack>
  );
};

export const PreviousBookingsStepFragmentString = graphql`
  fragment PreviousBookingsStepClientProfile on ClientProfile
  @argumentDefinitions(forSlotId: { type: "ID", defaultValue: null }) {
    member {
      ...PreviousBookingsListClient @arguments(forSlotId: $forSlotId)
    }
  }
`;

export const PreviousBookingsStepTimeSlotFragmentString = graphql`
  fragment PreviousBookingsStep_TimeSlot on TimeSlot {
    capacity(bookingType: $bookingType)
    usedCapacity
  }
`;
